/**
 * Schedule data helpers for role-specific filtering, milestone inference,
 * and item grouping without duplicating core schedule logic.
 */

import {
  type ScheduleItem,
  type ScheduleLink,
  type ScheduleSettings,
  type HolidayInfo,
  parseISODate,
  toISODate,
  countWorkingDaysInclusive,
} from './scheduleUtils';

export type ScheduleRole = 'management' | 'subcontractor' | 'client';

/** Words that indicate a major milestone in schedule item titles */
const MILESTONE_KEYWORDS = [
  'consent', 'slab', 'foundation', 'frame', 'framing', 'roof', 'roofing',
  'inspection', 'practical completion', 'ccc', 'handover', 'lock-up',
  'exterior', 'interior', 'cladding', 'windows', 'kitchen', 'bathroom',
  'landscaping', 'driveway', 'services', 'drainage', 'electrical', 'plumbing',
];

/**
 * Infer major milestones from included schedule items.
 * Uses: phase changes, completion status, critical items, keyword matches.
 */
export function inferMilestones(
  items: ScheduleItem[],
  criticalItemIds: Set<string>,
  maxCount: number = 8
): ScheduleItem[] {
  const included = items.filter((i) => i.is_included);
  if (included.length === 0) return [];

  const scored: { item: ScheduleItem; score: number }[] = [];

  // Phase first items
  const seenPhases = new Set<string>();
  for (const item of included) {
    if (item.phase && !seenPhases.has(item.phase)) {
      seenPhases.add(item.phase);
      scored.push({ item, score: 40 });
    }
  }

  // Completed items
  for (const item of included) {
    if (item.is_complete) {
      scored.push({ item, score: 30 });
    }
  }

  // Critical items
  for (const item of included) {
    if (criticalItemIds.has(item.id)) {
      scored.push({ item, score: 25 });
    }
  }

  // Keyword matches
  for (const item of included) {
    const titleLower = item.title.toLowerCase();
    const hasKeyword = MILESTONE_KEYWORDS.some((kw) => titleLower.includes(kw));
    if (hasKeyword) {
      scored.push({ item, score: 20 });
    }
  }

  // Accumulate scores per item
  const scoreMap = new Map<string, { item: ScheduleItem; score: number }>();
  for (const s of scored) {
    const existing = scoreMap.get(s.item.id);
    if (existing) {
      existing.score += s.score;
    } else {
      scoreMap.set(s.item.id, { item: s.item, score: s.score });
    }
  }

  // Sort by score descending, then by sort_order ascending
  const sorted = Array.from(scoreMap.values())
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      return a.item.sort_order - b.item.sort_order;
    });

  return sorted.slice(0, maxCount).map((s) => s.item);
}

/**
 * Get the estimated completion date: latest end_date among included items.
 */
export function getEstimatedCompletionDate(items: ScheduleItem[]): string | null {
  const included = items.filter((i) => i.is_included && i.end_date);
  if (included.length === 0) return null;
  const dates = included.map((i) => i.end_date as string);
  return dates.reduce((a, b) => (a > b ? a : b));
}

/**
 * Get the baseline completion date: latest baseline_end_date among included items.
 */
export function getBaselineCompletionDate(items: ScheduleItem[]): string | null {
  const included = items.filter((i) => i.is_included && i.baseline_end_date);
  if (included.length === 0) return null;
  const dates = included.map((i) => i.baseline_end_date as string);
  return dates.reduce((a, b) => (a > b ? a : b));
}

/**
 * Get upcoming schedule items: not complete, start_date >= today.
 * Sorted by start_date.
 */
export function getUpcomingScheduleItems(items: ScheduleItem[], limit: number = 10): ScheduleItem[] {
  const today = toISODate(new Date());
  const todayMs = parseISODate(today).getTime();
  return items
    .filter((i) => i.is_included && !i.is_complete && i.start_date && parseISODate(i.start_date).getTime() >= todayMs)
    .sort((a, b) => {
      const ta = a.start_date ? parseISODate(a.start_date).getTime() : 0;
      const tb = b.start_date ? parseISODate(b.start_date).getTime() : 0;
      return ta - tb;
    })
    .slice(0, limit);
}

/**
 * Get recently completed items with actual_end_date.
 * Sorted by actual_end_date descending.
 */
export function getCompletedScheduleItems(items: ScheduleItem[], limit: number = 5): ScheduleItem[] {
  return items
    .filter((i) => i.is_included && i.is_complete && i.actual_end_date)
    .sort((a, b) => {
      const ta = a.actual_end_date ? parseISODate(a.actual_end_date).getTime() : 0;
      const tb = b.actual_end_date ? parseISODate(b.actual_end_date).getTime() : 0;
      return tb - ta;
    })
    .slice(0, limit);
}

/**
 * Get the current active stage: first not-complete item by sort_order.
 */
export function getCurrentStage(items: ScheduleItem[]): ScheduleItem | null {
  const notComplete = items.filter((i) => i.is_included && !i.is_complete);
  if (notComplete.length === 0) return null;
  const sorted = [...notComplete].sort((a, b) => a.sort_order - b.sort_order);
  return sorted[0];
}

/**
 * Get the next stage after current.
 */
export function getNextStage(items: ScheduleItem[], current: ScheduleItem | null): ScheduleItem | null {
  if (!current) return null;
  const idx = items.findIndex((i) => i.id === current.id);
  if (idx >= 0 && idx + 1 < items.length) return items[idx + 1];
  return null;
}

/**
 * Collect a clean project-wide delay explanation from delayed items with delay_reason.
 */
export function getProjectDelayExplanation(items: ScheduleItem[]): string | null {
  const delayedItems = items.filter((i) => i.is_included && i.delay_status === 'delayed' && i.delay_reason);
  if (delayedItems.length === 0) return null;
  return delayedItems[0].delay_reason;
}

/**
 * Client-friendly schedule status label.
 */
export function getClientScheduleStatus(items: ScheduleItem[]): {
  label: string;
  color: string;
  bg: string;
} {
  const included = items.filter((i) => i.is_included);
  if (included.length === 0) {
    return { label: 'Not started', color: '#9CA3AF', bg: '#F9FAFB' };
  }

  const allComplete = included.every((i) => i.is_complete);
  if (allComplete) {
    return { label: 'Completed', color: '#059669', bg: '#F0FDF4' };
  }

  const delayedCount = included.filter((i) => i.delay_status === 'delayed').length;
  const atRiskCount = included.filter((i) => i.delay_status === 'at_risk').length;

  if (delayedCount > 0) {
    return { label: 'Slightly Delayed', color: '#F59E0B', bg: '#FEF0E7' };
  }
  if (atRiskCount > 0) {
    return { label: 'Watch', color: '#92400E', bg: '#FEF0E7' };
  }

  return { label: 'On Track', color: '#059669', bg: '#F0FDF4' };
}

/**
 * Schedule item visibility filter by role.
 * Management: all items.
 * Subcontractor: included only (assignment filtering done at component level).
 * Client: included only, and filter out very small internal tasks unless milestone/current/upcoming/delayed/completed.
 */
export function filterItemsByRole(
  items: ScheduleItem[],
  role: ScheduleRole,
  criticalItemIds?: Set<string>
): ScheduleItem[] {
  if (role === 'management') return items;

  const included = items.filter((i) => i.is_included);
  if (role === 'subcontractor') return included;

  // Client: hide very small internal tasks unless significant
  const cp = criticalItemIds || new Set<string>();
  const today = toISODate(new Date());
  const todayMs = parseISODate(today).getTime();

  return included.filter((item) => {
    // Always show these
    if (item.is_complete) return true;
    if (!item.is_complete && item.start_date && parseISODate(item.start_date).getTime() <= todayMs + 14 * 24 * 60 * 60 * 1000) return true;
    if (item.delay_status === 'delayed' || item.delay_status === 'at_risk') return true;
    if (cp.has(item.id)) return true;
    if (item.duration_days >= 3) return true;

    // Small internal task? Hide unless it has a milestone keyword
    const titleLower = item.title.toLowerCase();
    const hasKeyword = MILESTONE_KEYWORDS.some((kw) => titleLower.includes(kw));
    return hasKeyword;
  });
}

/**
 * Get progress percentage across included items.
 */
export function getScheduleProgress(items: ScheduleItem[]): { pct: number; completed: number; total: number } {
  const included = items.filter((i) => i.is_included);
  if (included.length === 0) return { pct: 0, completed: 0, total: 0 };
  const completed = included.filter((i) => i.is_complete).length;
  const pct = Math.round(included.reduce((s, i) => s + i.progress, 0) / included.length);
  return { pct, completed, total: included.length };
}

/**
 * Working-day variance between current end and baseline end.
 */
export function getScheduleVarianceDays(
  items: ScheduleItem[],
  settings: ScheduleSettings,
  holidays: HolidayInfo[]
): number {
  const currentEnd = getEstimatedCompletionDate(items);
  const baselineEnd = getBaselineCompletionDate(items);
  if (!currentEnd || !baselineEnd) return 0;
  try {
    let count = 0;
    const s = parseISODate(baselineEnd);
    const e = parseISODate(currentEnd);
    if (e < s) return -countWorkingDaysInclusive(toISODate(e), toISODate(s), settings, holidays);
    const d = new Date(s);
    const endIso = toISODate(e);
    while (true) {
      const iso = toISODate(d);
      if (isWorkingDaySimple(iso)) count++;
      if (iso === endIso) break;
      d.setDate(d.getDate() + 1);
    }
    return count;
  } catch {
    return Math.ceil((parseISODate(currentEnd).getTime() - parseISODate(baselineEnd).getTime()) / (1000 * 60 * 60 * 24));
  }
}

function isWorkingDaySimple(dateStr: string): boolean {
  const date = parseISODate(dateStr);
  const dow = date.getDay();
  return dow !== 0 && dow !== 6;
}

/**
 * Get linked stage names for a schedule item (client-friendly).
 */
export function getLinkedStageNames(
  item: ScheduleItem,
  links: ScheduleLink[],
  allItems: ScheduleItem[]
): { previous: string | null; next: string | null } {
  const prevLink = links.find((l) => l.successor_item_id === item.id);
  const nextLink = links.find((l) => l.predecessor_item_id === item.id);

  const previous = prevLink
    ? allItems.find((i) => i.id === prevLink.predecessor_item_id)?.title || null
    : null;
  const next = nextLink
    ? allItems.find((i) => i.id === nextLink.successor_item_id)?.title || null
    : null;

  return { previous, next };
}